import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";

/**
 * Wraps its children in a <div> that fades + slides up the first time it
 * enters the viewport. Triggered via IntersectionObserver; subsequent
 * scrolls don't re-animate. Uses CSS classes defined in index.css so the
 * bundle doesn't pull in framer-motion for a one-shot effect.
 *
 * `delay` (ms) staggers siblings — pass incrementing values to the children
 * of a section for a chained reveal.
 */
export function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    // Above-the-fold content: reveal immediately to avoid a visible "pop-in"
    // flash on first paint. Anything below is observed.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const style: CSSProperties | undefined = delay
    ? { transitionDelay: `${delay}ms` }
    : undefined;

  return (
    <div
      ref={ref}
      style={style}
      className={`reveal ${visible ? "revealed" : ""} ${className}`}
    >
      {children}
    </div>
  );
}
